'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { trackLandingCtaClicked } from '@/lib/analytics/posthog';

/**
 * Sticky mobile CTA bar — appears after the hero scrolls out of view.
 * Conversion-standard for mobile landing pages (Stripe, Cal.com, Vercel).
 *
 * 2026-05-26: switched from SignupModal-opener to direct /register link
 * (F-500 max-conversion consolidation). On mobile a full-page navigation
 * beats a modal anyway — the modal had to compete with mobile keyboards
 * for screen space and lost.
 *
 * Hidden on desktop (lg:hidden).
 * Uses scroll position rather than IntersectionObserver for predictability.
 */
export function StickyMobileCTA() {
  const t = useTranslations('landing.heroV3');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      // Trigger after ~75% of typical hero height
      setVisible(window.scrollY > 520);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div
      role="region"
      aria-label="Sticky call to action"
      className={`fixed inset-x-0 bottom-0 z-40 lg:hidden motion-safe:transition-all motion-safe:duration-300 ${
        visible
          ? 'opacity-100 translate-y-0 pointer-events-auto'
          : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    >
      <div className="border-t border-gray-200/80 bg-white/95 px-4 py-3 backdrop-blur-md shadow-[0_-4px_16px_-4px_rgba(0,0,0,0.06)]">
        <a
          href="/register?source=sticky-mobile"
          onClick={() => trackLandingCtaClicked('sticky-mobile')}
          className="flex h-12 w-full items-center justify-center rounded-full bg-gray-950 text-sm font-semibold text-white shadow-sm hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2"
        >
          {t('ctaPrimary')} →
        </a>
      </div>
    </div>
  );
}
